import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Button, Table } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import { array, func } from 'prop-types';
import { startSetSites, startRemoveSite, startEditSite, startAddSite } from '../../actions/sites';
import SourcesListItem from './SourcesListItem';
import Card from '../Card/Card';
import EditSourceModal from '../../modals/EditSourceModal/EditSourceModal';
import DialogModal from '../../modals/DialogModal/DialogModal';
import LayoutCol from '../Layout/LayoutCol';
import AddSourceForm from './AddSourceForm';

class SourcesList extends Component {
  static propTypes = {
    settings: array.isRequired,
    sites: array.isRequired,
    showAlert: func.isRequired,
  };

  static defaultProps = {
    settings: [],
    sites: [],
  };

  state = {
    loading: false,
    isAddModalOpen: false,
    editModal: false,
    removeModal: false,
    site: null,
    types: [],
    sortConfig: null,
  };

  componentDidMount() {
    this._mounted = true;

    this.props.startSetSites().then(() => {
      this._mounted &&
        this.setState((prevState) => ({
          ...prevState,
          loading: false,
        }));
    });
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  controls = [
    <Button
      className="btn btn-sm"
      color="primary"
      onClick={() => {
        this.toggleAddModal();
      }}
    >
      <FormattedMessage id="sources.buttons.add-source" defaultMessage="Add Source" />
    </Button>,
  ];

  get types() {
    return this.props.settings.reduce((types, { id, title }) => ({ ...types, [id]: title }), {});
  }

  handleEditCancel = () => {
    this.setState(() => ({ site: null }));
  };

  toggleEditModal = () => {
    this.setState((prevState) => ({ editModal: !prevState.editModal }));
  };

  toggleAddModal = () => {
    this.setState((prevState) => ({
      isAddModalOpen: !prevState.isAddModalOpen,
    }));
  };

  handleSiteEdit = (site) => {
    this.setState(() => ({ site, editModal: true }));
  };

  handleSubmit = ({ id, title }) => {
    this.setState(() => ({ loading: true }));

    this.props
      .startEditSite(id, { title })
      .then(() => this.toggleEditModal())
      .then(() => this.setState(() => ({ loading: false })));
  };

  handleSiteRemove = (id) => {
    this.setState(() => ({ site: { id }, removeModal: true }));
  };

  closeRemoveModal = () => {
    this.setState(() => ({ removeModal: false, site: null }));
  };

  removeSite = () => {
    const { site = {} } = this.state;
    this.setState(() => ({ loading: true, removeModal: false }));

    this.props.startRemoveSite(site.id).then((resp) => {
      if (resp.type === 'REMOVE_SITE_FAILURE') {
        this.props.showAlert({
          alertType: 'danger',
          message: resp.payload,
          dismissable: false,
          closeInSeconds: 5,
        });
      }
      this.setState(() => ({ loading: false, site: null }));
    });
  };

  submit = (data) => {
    this.props.startAddSite(data).then(() => {
      this.toggleAddModal();
      this.props.startSetSites();
    });
  };

  handleHeaderClick = (column) => {
    this.setState((prevState) => {
      if (prevState.sortConfig?.column === column) {
        return {
          sortConfig: {
            column,
            direction: prevState.sortConfig.direction === 'asc' ? 'desc' : 'asc',
          },
        };
      }
      return { sortConfig: { column, direction: 'asc' } };
    });
  };

  getSortedSites = () => {
    const { sites } = this.props;
    const { sortConfig } = this.state;

    if (!sortConfig) return sites;

    return [...sites].sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.column) {
        case 'id':
          aVal = a.id;
          bVal = b.id;
          break;
        case 'title':
          aVal = a.title.toLowerCase();
          bVal = b.title.toLowerCase();
          break;
        case 'type':
          aVal = this.types[a.type].toLowerCase();
          bVal = this.types[b.type].toLowerCase();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  render() {
    const { site, loading, editModal, removeModal, isAddModalOpen, sortConfig } = this.state;
    const sortedSites = this.getSortedSites();

    return (
      <LayoutCol>
        <Card.WithControls
          title={'sources.header.title'}
          defaultMessage="Sources"
          loading={loading}
          controls={this.controls}
        >
          <Card.Body className="no-padding">
            {sortedSites.length > 0 ? (
              <Table className="sources-table no-margin" bordered responsive>
                <thead>
                  <tr>
                    <th
                      className={sortConfig?.column === 'id' ? sortConfig.direction : ''}
                      onClick={() => this.handleHeaderClick('id')}
                    >
                      <FormattedMessage id="sources.table.id" defaultMessage="ID" />
                    </th>
                    <th
                      className={sortConfig?.column === 'title' ? sortConfig.direction : ''}
                      onClick={() => this.handleHeaderClick('title')}
                    >
                      <FormattedMessage id="sources.table.title" defaultMessage="Title" />
                    </th>
                    <th
                      className={sortConfig?.column === 'type' ? sortConfig.direction : ''}
                      onClick={() => this.handleHeaderClick('type')}
                    >
                      <FormattedMessage
                        id="sources.table.traffic-source"
                        defaultMessage="Traffic source"
                      />
                    </th>
                    <th className="table-controls" />
                    <th className="table-controls" />
                  </tr>
                </thead>
                <tbody>
                  {sortedSites.map((site) => (
                    <SourcesListItem
                      key={site.id}
                      types={this.types}
                      onEdit={this.handleSiteEdit}
                      onRemove={this.handleSiteRemove}
                      {...site}
                    />
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="card-empty">
                <FormattedMessage id="sources.no-sources" defaultMessage="No Sources" />
              </div>
            )}
          </Card.Body>

          <Modal
            modalClassName="modal-add-sources"
            toggle={this.toggleAddModal}
            isOpen={isAddModalOpen}
            size="lg"
            centered
          >
            <ModalHeader toggle={this.toggleAddModal}>
              <FormattedMessage id="source.create" defaultMessage="Create Source" />
            </ModalHeader>
            <ModalBody>
              <AddSourceForm
                onCancel={this.toggleAddModal}
                types={this.props.settings}
                submit={this.submit}
              />
            </ModalBody>
          </Modal>

          <EditSourceModal
            loading={loading}
            isOpen={editModal}
            source={site}
            toggleModal={this.toggleEditModal}
            onSubmit={this.handleSubmit}
            onClosed={this.handleEditCancel}
          />

          <DialogModal
            className="source-dialog-modal"
            open={removeModal}
            title={<FormattedMessage id="sources.modal.title" defaultMessage="Remove source?" />}
            message={<FormattedMessage id="sources.modal.remove" defaultMessage="Remove source?" />}
            onCancel={this.closeRemoveModal}
            onConfirm={this.removeSite}
            cancelText={<FormattedMessage id="sources.modal.cancel" defaultMessage="Cancel" />}
            confirmText={<FormattedMessage id="sources.modal.confirm" defaultMessage="Remove" />}
          />
        </Card.WithControls>
      </LayoutCol>
    );
  }
}

const mapStateToProps = (state) => ({
  theme: state.webmaster.profile.theme,
  sites: state.webmaster.sites,
  settings: state.app.settings.site,
});

const mapDispatchToProps = (dispatch) => ({
  startSetSites: (params) => dispatch(startSetSites(params)),
  startEditSite: (id, updates) => dispatch(startEditSite(id, updates)),
  startRemoveSite: (siteId) => dispatch(startRemoveSite(siteId)),
  startAddSite: (siteData) => dispatch(startAddSite(siteData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SourcesList);
